// eslint-disable-next-line max-classes-per-file
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { renderToStaticMarkup } from 'react-dom/server';
import Form from '../../commonComponents/forms/Form';
import translate from '../../commonComponents/translate';
import Title from '../../commonComponents/Title';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import userProp from '../../commonComponents/propTypes/userProp';
import i18n from '../../services/i18n';
import List from '../../commonComponents/lists/ClientSideList';
import {
  findCurrencySymbol,
  formatPrice,
  handleBillingAddressInOrder,
  handleShippingAddressInOrder,
} from '../../services/utilityFunctions';
import DisplayShippingContainer from './Components/DisplayShippingContainer/DisplayShippingContainer';
import Notifications from '../../services/notifications';
import CurrencyProp from '../profile/propTypes/currencyProp';
import { useModal } from '../../services/customHooks';
import RemoveProductVariationModal from './Components/RemoveProductVariationModal';
import Loader from '../../services/loader';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import AddAddressModal from '../sessions/modal/AddAddressModal';
import VatTypesProp from '../../commonComponents/forms/Address/VatTypesProp';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import { PAYMENT_PROVIDER } from '../../commonComponents/propTypes/tenantProp';
import ConfigsContext from '../../contextProviders/ConfigsContext';
import CustomerSection from './Components/CustomerSection';
import OperatorSection from './Components/OperatorSection';
import CategoryProp from '../academy/propTypes/categoryProp';
import WarningSection from './Components/modal/components/WarningSection';
import BasicModal from '../../commonComponents/modal/BasicModal';
import AddNewProductModal from './Components/modal/AddNewProductModal';
import {
  getInitialOperator,
  getInitialShipping,
  getInitialState,
  handleValidationFrequencies,
  MODE,
  getProductsForOrderCreate,
} from './Components/helpers/orderHelper';
import FormModal from '../../commonComponents/modal/FormModal';
import FormButton from '../../commonComponents/buttons/FormButton';
import User from '../../../../../domain/models/user';
import { validateAddressFields } from '../../commonComponents/helpers/addressHelper';
import { normalizeNumber } from '../../services/numberUtilis';
import { PROVIDER_TYPE } from '../../commonComponents/propTypes/marketProductForm';
import PdfFormModal from '../../commonComponents/modal/PdfFormModal';
import OrderCreateFooter from './Components/OrderCreateFooter';
import DocumentsTab from '../shop/detail/components/DocumentsTab';
import RemoveDocumentModal from '../../commonComponents/modal/RemoveDocumentModal';
import { OPPORTUNITIES_STATUS } from '../opportunities/components/helpers/opportunitiesListHelper';
import OrderProp from '../shop/propTypes/orderProp';
import ProductItem from './Components/ProductItem';
import AddPackProduct from '../shop/shopItems/pack/advancedPackComponents/AddPackProduct';
import { PACK_TYPE } from '../shop/propTypes/productProp';
import SubscriptionProp from '../shop/propTypes/subscriptionProp';

const fetchUser = async userId => {
  try {
    const { data: response } = await axios.get(`/api/user/${userId}`, {
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'Organization',
      },
    });
    return response;
  } catch (err) {
    console.log('failed to fetch user: ', err);
    return null;
  }
};

const getUserTypeForSelector = () => {
  const userTypeNames = {};
  Object.values(userProp.TYPES).forEach(userType => {
    userTypeNames[userType] = i18n.__(`user_type_${userType}`);
  });
  return userTypeNames;
};

const notValidProductForms = products => {
  const formNames = [];
  products.forEach(product => {
    if (product?.form && !product.formAnswerData) {
      formNames.push(product?.form?.name);
    }
  });

  return formNames?.length > 0 ? formNames?.join('. ')?.slice(0, -2) : null;
};

const initialTotalsState = {
  total: 0,
  subtotal: 0,
  shippingTotal: 0,
  shippingTax: 0,
  discount: 0,
  userDiscount: 0,
  userDiscountPercentage: 0,
  tax: 0,
  currency: 'eur',
};

const distributeVariationsAcrossSections = (
  sections,
  variationId,
  totalQuantity,
) => {
  if (!sections || sections.length === 0) {
    return [
      {
        _id: variationId,
        quantity: totalQuantity,
        variation: { _id: variationId },
      },
    ];
  }

  const result = [];
  let remainingQuantity = totalQuantity;

  // Find all sections that contain this variation
  const sectionsWithVariation = sections.filter(section =>
    section.packVariationsAvailable?.includes(variationId),
  );

  if (sectionsWithVariation.length === 0) {
    return [];
  }

  // Distribute quantity across sections
  sectionsWithVariation.forEach((section, index) => {
    const isLastSection = index === sectionsWithVariation.length - 1;
    const quantityForSection = isLastSection
      ? remainingQuantity
      : Math.min(section.amountVariationsToChoose || 1, remainingQuantity);

    if (quantityForSection > 0) {
      result.push({
        _id: variationId,
        quantity: quantityForSection,
        sectionId: section._id,
        sectionName: section.name,
        variation: { _id: variationId },
        inside: true,
      });

      remainingQuantity -= quantityForSection;
    }
  });

  return result;
};

function calculateReverseHybridPackQuantity(
  purchasedPackVariations,
  originalProduct,
) {
  const staticPackVariations = originalProduct.packVariations ?? [];
  const layers = originalProduct.multiPackVariationsAvailable ?? [];

  // Per your rule
  if (staticPackVariations.length === 0 && layers.length === 0) {
    return 0;
  }

  // Build purchased quantity map
  const purchasedQty = new Map();
  for (const { variation, quantity } of purchasedPackVariations ?? []) {
    if (!variation) {
      // eslint-disable-next-line no-continue
      continue;
    }
    const q = Number(quantity) || 0;
    if (q <= 0) {
      // eslint-disable-next-line no-continue
      continue;
    }
    purchasedQty.set(variation, (purchasedQty.get(variation) || 0) + q);
  }

  /**
   * If static variations exist, they determine the multiplier uniquely
   * (all ratios bought/base must be equal, and each bought must be exact multiple).
   */
  const inferMultiplierFromStatic = () => {
    let m = null;

    // Build set of variation IDs that also appear in any dynamic layer
    const dynamicLayerVarIds = new Set();
    for (const layer of layers) {
      for (const v of layer.packVariationsAvailable ?? []) {
        dynamicLayerVarIds.add(typeof v === 'string' ? v : v._id);
      }
    }

    for (const s of staticPackVariations) {
      const id =
        typeof s.variation === 'string' ? s.variation : s.variation._id;
      const base = Number(s.quantity) || 0;
      if (!id || base <= 0) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const bought = purchasedQty.get(id);
      if (bought == null || bought <= 0) {
        return null;
      }

      // If this variation also appears in dynamic layers, bought can exceed
      // m * base (the surplus goes to layers). So we only require bought >= m * base
      // and bought % base === 0 is NOT required — we derive m as floor(bought / base)
      // but must be consistent across all pure-static variations.
      const isAlsoInDynamicLayer = dynamicLayerVarIds.has(id);

      if (isAlsoInDynamicLayer) {
        // bought must be at least base (m >= 1), but can be more.
        // We can't infer m from this variation alone — skip for ratio determination.
        // eslint-disable-next-line no-continue
        continue;
      }

      // Pure static variation: bought must be exact multiple of base
      if (bought % base !== 0) {
        return null;
      }

      const ratio = bought / base;
      if (m == null) {
        m = ratio;
      } else if (m !== ratio) {
        return null;
      }
    }

    return m;
  };

  // ---------------- Max-Flow (Dinic) ----------------

  class Edge {
    constructor(to, rev, cap) {
      this.to = to;
      this.rev = rev;
      this.cap = cap;
    }
  }

  class Dinic {
    constructor(n) {
      this.n = n;
      this.g = Array.from({ length: n }, () => []);
      this.level = new Array(n).fill(-1);
      this.it = new Array(n).fill(0);
    }

    addEdge(fr, to, cap) {
      const fwd = new Edge(to, this.g[to].length, cap);
      const rev = new Edge(fr, this.g[fr].length, 0);
      this.g[fr].push(fwd);
      this.g[to].push(rev);
    }

    bfs(s, t) {
      this.level.fill(-1);
      const q = [];
      this.level[s] = 0;
      q.push(s);

      while (q.length) {
        const v = q.shift();
        for (const e of this.g[v]) {
          if (e.cap > 0 && this.level[e.to] < 0) {
            this.level[e.to] = this.level[v] + 1;
            q.push(e.to);
          }
        }
      }
      return this.level[t] >= 0;
    }

    dfs(v, t, f) {
      if (v === t) {
        return f;
      }
      for (; this.it[v] < this.g[v].length; this.it[v] += 1) {
        const e = this.g[v][this.it[v]];
        if (e.cap <= 0) {
          // eslint-disable-next-line no-continue
          continue;
        }
        if (this.level[e.to] !== this.level[v] + 1) {
          // eslint-disable-next-line no-continue
          continue;
        }
        const ret = this.dfs(e.to, t, Math.min(f, e.cap));
        if (ret > 0) {
          e.cap -= ret;
          this.g[e.to][e.rev].cap += ret;
          return ret;
        }
      }
      return 0;
    }

    maxFlow(s, t) {
      let flow = 0;
      const INF = 1e18;
      while (this.bfs(s, t)) {
        this.it.fill(0);
        let f;
        // eslint-disable-next-line no-cond-assign
        while ((f = this.dfs(s, t, INF)) > 0) {
          flow += f;
        }
      }
      return flow;
    }
  }

  /**
   * Feasibility check for a given multiplier m using max-flow allocation.
   * Static quantities are consumed first (exactly), then remaining quantities must satisfy layer demands.
   */
  const isFeasible = m => {
    if (!Number.isInteger(m) || m <= 0) {
      return false;
    }

    // Build set of variation IDs that also appear in any dynamic layer
    const dynamicLayerVarIds = new Set();
    for (const layer of layers) {
      for (const v of layer.packVariationsAvailable ?? []) {
        dynamicLayerVarIds.add(typeof v === 'string' ? v : v._id);
      }
    }

    const remainingQty = new Map(purchasedQty);

    for (const s of staticPackVariations) {
      const id =
        typeof s.variation === 'string' ? s.variation : s.variation._id;
      const base = Number(s.quantity) || 0;
      if (!id || base <= 0) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const required = m * base;
      const bought = remainingQty.get(id) || 0;

      const isAlsoInDynamicLayer = dynamicLayerVarIds.has(id);

      if (isAlsoInDynamicLayer) {
        // Must have AT LEAST the static requirement; surplus goes to layers
        if (bought < required) {
          return false;
        }
        remainingQty.set(id, bought - required); // surplus remains for layer allocation
      } else {
        // Pure static: must match exactly
        if (bought !== required) {
          return false;
        }
        remainingQty.set(id, 0);
      }
    }

    // If no layers, static alone fully defines feasibility
    if (layers.length === 0) {
      return true;
    }

    // 2) Build a flow network for layers allocation (handles overlaps correctly)
    // Nodes: source -> variation nodes -> layer nodes -> sink
    const validLayers = layers.filter(
      l =>
        (Number(l.amountVariationsToChoose) || 0) > 0 &&
        (l.packVariationsAvailable?.length || 0) > 0,
    );

    // If layers exist but none are valid (bad data), treat as infeasible
    if (validLayers.length === 0) {
      return false;
    }

    // Collect all variation ids that appear in any layer (union)
    const layerVarSet = new Set();
    for (const layer of validLayers) {
      for (const v of layer.packVariationsAvailable ?? []) {
        layerVarSet.add(typeof v === 'string' ? v : v._id);
      }
    }

    // Variation nodes only for those with remainingQty > 0 and that exist in the union set
    const variationIds = [];
    for (const v of layerVarSet) {
      const q = remainingQty.get(v) || 0;
      if (q > 0) {
        variationIds.push(v);
      }
    }

    // Total demand required by layers
    const demands = validLayers.map(
      l => m * (Number(l.amountVariationsToChoose) || 0),
    );
    const totalDemand = demands.reduce((a, b) => a + b, 0);

    // Quick fail: not enough quantity in union
    const totalSupply = variationIds.reduce(
      (sum, v) => sum + (remainingQty.get(v) || 0),
      0,
    );
    if (totalSupply < totalDemand) {
      return false;
    }

    const V = variationIds.length;
    const L = validLayers.length;

    const SRC = 0;
    const varOffset = 1;
    const layerOffset = varOffset + V;
    const SNK = layerOffset + L;
    const N = SNK + 1;

    const dinic = new Dinic(N);

    // Source -> variation capacity = remaining qty
    for (let i = 0; i < V; i += 1) {
      const vId = variationIds[i];
      dinic.addEdge(SRC, varOffset + i, remainingQty.get(vId) || 0);
    }

    // variation -> layer edges if variation is available in that layer
    // (capacity INF; availability is the only constraint besides total quantities/demands)
    const INF = 1e12;

    // Build a fast lookup per layer for membership
    const layerAvailSets = validLayers.map(
      l =>
        new Set(
          (l.packVariationsAvailable ?? []).map(v =>
            typeof v === 'string' ? v : v._id,
          ),
        ),
    );

    for (let i = 0; i < V; i += 1) {
      const vId = variationIds[i];
      for (let j = 0; j < L; j += 1) {
        if (layerAvailSets[j].has(vId)) {
          dinic.addEdge(varOffset + i, layerOffset + j, INF);
        }
      }
    }

    // layer -> sink capacity = demand
    for (let j = 0; j < L; j += 1) {
      dinic.addEdge(layerOffset + j, SNK, demands[j]);
    }

    const flow = dinic.maxFlow(SRC, SNK);
    return flow === totalDemand;
  };

  // ---------------- Find the best multiplier ----------------

  // If static gives a unique multiplier, just validate it (and return it if feasible)
  const staticM = inferMultiplierFromStatic();
  if (staticM != null) {
    return isFeasible(staticM) ? staticM : 0;
  }

  // No static => infer from layers only
  // Upper bound: based on total supply in union set divided by per-pack demand sum(amountToChoose)
  const validLayers = layers.filter(
    l =>
      (Number(l.amountVariationsToChoose) || 0) > 0 &&
      (l.packVariationsAvailable?.length || 0) > 0,
  );
  if (validLayers.length === 0) {
    return 0;
  }

  const perPackDemand = validLayers.reduce(
    (sum, l) => sum + (Number(l.amountVariationsToChoose) || 0),
    0,
  );
  if (perPackDemand <= 0) {
    return 0;
  }

  const union = new Set();
  for (const layer of validLayers) {
    for (const v of layer.packVariationsAvailable ?? []) {
      union.add(typeof v === 'string' ? v : v._id);
    }
  }

  const totalUnionSupply = Array.from(union).reduce(
    (sum, v) => sum + (purchasedQty.get(v) || 0),
    0,
  );

  const maxM = Math.floor(totalUnionSupply / perPackDemand);
  if (maxM <= 0) {
    return 0;
  }

  // Optional tightening with a gcd over total supply / perPackDemand is NOT safe with overlaps,
  // so we just search downwards (usually small).
  for (let m = maxM; m >= 1; m -= 1) {
    if (isFeasible(m)) {
      return m;
    }
  }

  return 0;
}

const handleSubscriptionProducts = orderSubscription => {
  const { products } = orderSubscription;

  if (!products) {
    return [];
  }

  // Mirror handleAddingProducts so subscription-loaded rows carry the digital
  // flag too. Without it, isOnlyDigitalProducts stays false for a digital-only
  // subscription, the shipping selector is shown, and a provider can be sent
  // and persisted onto the subscription / charged on every renewal.
  const withDigitalContent = list =>
    list.map(product => ({
      ...product,
      isDigitalContent: product?.parentProduct
        ? product?.parentProduct?.isDigital
        : !!(
            product?.digitalDocument &&
            product?.digitalDocument?.url?.length > 0
          ),
    }));

  const hybridPacks = products.filter(p => p.type === PACK_TYPE.HYBRID_PACK);

  if (hybridPacks.length > 0) {
    // Group hybrid packs by packProduct._id
    const groupedByPack = hybridPacks.reduce((acc, p) => {
      const packId = p.packProduct?._id;

      if (!packId) {
        return acc;
      }

      if (!acc[packId]) {
        acc[packId] = {
          ...p,
          ...p.product,
          ...p.packProduct,
          originalProduct: p.packProduct,
          packVariations: [],
          cartVariations: [],
          inside: true,
          userPriceToUse: {
            price: 0,
            total: 0,
            tax: p?.tax ?? 0,
            code: p?.product?.userPrice?.code ?? null,
            symbol: p?.product?.userPrice?.symbol ?? null,
          },
        };
      }

      // Distribute variations across sections
      const distributedVariations = distributeVariationsAcrossSections(
        p.packProduct?.multiPackVariationsAvailable || [],
        p.product?._id,
        p.quantity || 1,
      );

      acc[packId].userPriceToUse.price += (p?.price ?? 0) * (p?.quantity || 1);
      acc[packId].userPriceToUse.total += (p?.total ?? 0) * (p?.quantity || 1);
      // Add the distributed variations
      acc[packId].packVariations.push(...distributedVariations);
      acc[packId].cartVariations.push({
        variation: p.product?._id,
        quantity: p.quantity || 1,
      });

      return acc;
    }, {});

    const formattedHybridPacks = Object.values(groupedByPack).map(pack => {
      const packQuantity = calculateReverseHybridPackQuantity(
        pack.cartVariations,
        pack.packProduct,
      );

      // Calculate hybrid pack quantities per section
      const hybridPackQuantitiesPerSection = (() => {
        if (
          !pack.multiPackVariationsAvailable ||
          pack.multiPackVariationsAvailable.length === 0
        ) {
          return [];
        }

        // Build a map of static variation ID -> required quantity per pack
        const staticRequirements = new Map();
        (pack.packProduct?.packVariations || []).forEach(pv => {
          const id =
            typeof pv.variation === 'string'
              ? pv.variation
              : pv.variation?._id ?? '';
          if (id) {
            staticRequirements.set(id, pv.quantity);
          }
        });

        // Populate remainingQuantities with only the SURPLUS above static requirements
        const remainingQuantities = new Map();
        pack.cartVariations
          ?.map(v => ({ ...v, quantity: v.quantity / packQuantity }))
          ?.forEach(pv => {
            const staticQty = staticRequirements.get(pv.variation) || 0;
            const surplus = pv.quantity - staticQty;
            if (surplus > 0) {
              remainingQuantities.set(pv.variation, surplus);
            }
          });

        const result = [];

        // Build quantities for each section by distributing remaining quantities
        pack.multiPackVariationsAvailable.forEach(section => {
          section.packVariationsAvailable.forEach(variationId => {
            const variation =
              typeof variationId === 'string' ? variationId : variationId._id;

            if (!variation) return;

            const remaining = remainingQuantities.get(variation) || 0;

            if (remaining > 0) {
              // Find all sections that contain this variation
              const sectionsWithVariation =
                pack.multiPackVariationsAvailable.filter(s =>
                  s.packVariationsAvailable?.some(
                    v => (typeof v === 'string' ? v : v._id) === variation,
                  ),
                );

              const currentSectionIndex = sectionsWithVariation.findIndex(
                s => s._id === section._id,
              );

              if (currentSectionIndex !== -1) {
                const isLastSection =
                  currentSectionIndex === sectionsWithVariation.length - 1;
                const amountToChoose = section.amountVariationsToChoose || 1;
                const allocated = isLastSection
                  ? remaining
                  : Math.min(amountToChoose, remaining);

                if (allocated > 0) {
                  result.push({
                    _id: variation,
                    quantity: allocated,
                    sectionId: section._id,
                    sectionName: section.name,
                    variation: { _id: variation },
                    inside: true,
                  });
                  remainingQuantities.set(variation, remaining - allocated);
                }
              }
            }
          });
        });

        return result;
      })();

      return {
        ...pack,
        type: pack.type,
        _id: pack._id,
        packProduct: pack.packProduct,
        packVariations: hybridPackQuantitiesPerSection,
        quantityToOrder: packQuantity,
        packType: pack?.packProduct?.pack?.packType ?? null,
        amountVariationsPerAdvancedPack:
          pack?.packProduct?.pack?.amountVariationsPerAdvancedPack ?? null,
        frequency: orderSubscription.frequency,
        parentProduct: {
          ...pack.packProduct,
        },
      };
    });

    // Get all non-hybrid products
    const nonHybridProducts = products
      .filter(p => p.type !== PACK_TYPE.HYBRID_PACK)
      .map(p => ({
        ...(p.product || {}),
        ...(p.packProduct || {}),
        quantityToOrder: p.quantity,
        userPriceToUse:
          p.type === 'product'
            ? p.product?.userPrice
            : p?.packProduct?.pack?.userPrice,
        frequency: orderSubscription.frequency,
        parentProduct: orderSubscription.productParent,
        isPack: p.type !== 'product',
        packType: p?.packProduct?.pack?.packType ?? null,
        amountVariationsPerAdvancedPack:
          p?.packProduct?.pack?.amountVariationsPerAdvancedPack ?? null,
        packVariations:
          p?.packVariations?.map(pv => ({
            ...pv,
            quantity: pv.quantity,
            inside: true,
            _id: p.variation?._id || p.variation,
          })) || [],
      }));

    return withDigitalContent([...formattedHybridPacks, ...nonHybridProducts]);
  }

  return withDigitalContent(
    products.map(p => {
      const { type } = p;

      return {
        ...p,
        ...(p.product || {}),
        ...(p.packProduct || {}),
        quantityToOrder: p.quantity,
        userPriceToUse:
          type === 'product'
            ? p.product?.userPrice
            : p?.packProduct?.pack?.userPrice,
        frequency: orderSubscription.frequency,
        parentProduct: orderSubscription.productParent,
        isPack: type !== 'product',
        packType: p?.packProduct?.pack?.packType ?? null,
        amountVariationsPerAdvancedPack:
          p?.packProduct?.pack?.amountVariationsPerAdvancedPack ?? null,
        packVariations:
          p?.packVariations?.map(pv => ({
            ...pv,
            quantity: pv.quantity,
            inside: true,
            _id: p.variation?._id || p.variation,
          })) || [],
      };
    }),
  );
};

function OrderCreate({
  t,
  owner,
  currencies,
  countries,
  vatTypes,
  _csrf,
  customer,
  categories,
  catalogTypes,
  isOpportunity,
  languages,
  opportunity,
  mode,
  orderSubscription,
}) {
  /** Select required */
  const userTypeNames = getUserTypeForSelector();
  const [products, setProducts] = useState(
    orderSubscription || opportunity
      ? handleSubscriptionProducts(orderSubscription || opportunity)
      : [],
  );
  // Latest-products ref: the jQuery table handlers (e.g. ".btn-fill-form") are
  // bound once and would otherwise close over a stale `products` array — so after
  // a form is saved they'd re-open it without the just-saved formAnswerData.
  // Reading productsRef.current always gives the current products.
  const productsRef = useRef(products);
  productsRef.current = products;
  const { loggedUser } = useContext(LoggedUserContext);
  const isEdit = mode === MODE.EDIT;
  const { tenant } = useContext(ConfigsContext);
  const { directSellingSettings, directSelling } = tenant?.settings || {};
  const { CVIntegration, VAIntegration } = directSellingSettings || {};
  const isOwnerACollaborator =
    loggedUser?.type === User.TYPE.COLLABORATOR ||
    loggedUser?.role === User.ROLE.ADMIN;

  const showVA = directSelling && VAIntegration && isOwnerACollaborator;
  const showCV = directSelling && CVIntegration && isOwnerACollaborator;

  const { paymentProviderType } = tenant.settings || {};

  // Get different information when opportunity or order
  const pageInformation = useMemo(() => {
    if (orderSubscription) {
      return {
        title: t('order.subscription.edit'),
        successMessage: t('order.subscription.edit.success'),
        confirmButton: t('edit'),
        confirmClass: 'btn btn-info',
        calculateEndpoint:
          '/api/market/order/calculate?populate=products.product',
        createEndpoint: `/api/market/order/subscription/${orderSubscription._id}/content?`,
        goBackUrl: `/shop/order/subscription/${orderSubscription._id}`,
      };
    }

    const opportunityEndpoint = isEdit
      ? `/api/market/opportunity/${opportunity?._id}?`
      : '/api/market/opportunity?';
    const confirmButtonOpportunity =
      mode === MODE.CREATE ? t('place.offer') : t('edit.offer');
    const titleOpportunity =
      mode === MODE.CREATE ? t('opportunity.create') : t('edit.offer');
    return {
      title: !isOpportunity ? t('order.create') : titleOpportunity,
      confirmButton: !isOpportunity
        ? t('order_place_order')
        : confirmButtonOpportunity,
      confirmClass: !isOpportunity ? 'btn btn-dynamic' : 'btn btn-info',
      calculateEndpoint: !isOpportunity
        ? '/api/market/order/calculate?populate=products.product'
        : '/api/market/opportunity/calculate?populate=products.product',
      createEndpoint: !isOpportunity
        ? '/api/market/order?'
        : opportunityEndpoint,
      goBackUrl: !isOpportunity ? '/shop/order' : '/shop/opportunities',
    };
  }, [isOpportunity, t, opportunity, mode]);

  /** Init Variables */
  const formId = 'createOrder';
  const tableId = 'createOrderList';
  const tableEl = useRef(null);
  const validationRules = useMemo(() => {
    const rules = {};
    if (isOpportunity) rules.opportunityName = { required: true, minlength: 3 };
    return rules;
  }, [isOpportunity]);
  // Flag to alert if product is digital or not in step 2
  const [isOnlyDigitalProducts, setIsOnlyDigitalProducts] = useState(false);

  const loader = useRef(new Loader('#orderCreateFooter'));
  const loaderProducts = useRef(new Loader('#products'));
  const operatorLoader = useRef(new Loader('#operatorContainer'));
  const customerLoader = useRef(new Loader('#customerContainer'));

  /** Initial State */
  const [isLoadingBtn, setIsLoadingBtn] = useState(false);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [showRemoveDocument, setShowRemoveDocument] = useModal({
    show: false,
    data: null,
  });
  const [showSendEmailModal, setShowSendEmailModal] = useModal({
    show: false,
    data: null,
  });
  const [isSendOfferModalLoading, setIsSendOfferModalLoading] = useState(false);
  const [checkedTerms, setCheckedTerms] = useState(false);
  // Allow us to get the customer when is from chat and then we are free to select any other team member
  const [getCustomer, setGetCustomer] = useState(customer);
  const [showSelectVariationsModal, setShowSelectVariationsModal] = useModal({
    show: false,
    data: null,
  });
  // Add Product Modal
  const [addProductModal, setAddProductModal] = useModal({
    show: false,
    data: null,
  });
  // Warning Popup
  const [warningModal, setWarningModal] = useModal({
    show: false,
  });

  // region Form Popup
  const [formModal, setFormModal] = useModal({
    show: false,
    data: null,
  });

  const handleFormSave = (productId, formAnswers, formAnswersLink = null) => {
    if (productId) {
      setProducts(prevProducts => {
        const newProducts = prevProducts.map(product => {
          // Check if the product _id matches the specified productId
          if (product._id === productId) {
            // Replace the formAnswerData with the provided formAnswers
            return {
              ...product,
              formAnswerData: formAnswers,
              formAnswersLink,
            };
          }
          return product; // Return unchanged product if _id doesn't match
        });

        tableEl.current.setList(newProducts);
        return newProducts;
      });
    }
    setFormModal({
      show: false,
      data: null,
    });
  };

  // endregion Form Popup

  const [operator, setOperator] = useState(
    getInitialOperator(mode, isOpportunity, opportunity)?.operator,
  );
  const [operatorAddress, setOperatorAddress] = useState(
    getInitialOperator(mode, isOpportunity, opportunity)?.installationAddress,
  );

  const [operatorAddressModal, showOperatorAddressModal] = useModal({
    show: false,
  });
  const [addressModal, showAddressModal] = useModal({
    show: false,
    data: null,
  });

  // We only display the operator block if operators exist for this organization
  // We do an initial request to guarantee there's at least one
  const [hasOperators, setHasOperators] = useState(false);
  const displayOperators = hasOperators && !isOnlyDigitalProducts;
  useEffect(() => {
    let cancelled = false;

    axios
      .get('/user/table/list', {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        },
        params: {
          isOperator: true,
          page: 0,
          length: 1,
        },
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'error_fetchOperators');
        return null;
      })
      .then(result => {
        if (!cancelled) {
          setHasOperators(!!result?.data?.data?.length);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleOperatorChange = useCallback(operatorId => {
    operatorLoader.current.show();

    fetchUser(operatorId).then(response => {
      operatorLoader.current.hide();
      setOperator(response?.data || null);
    });
  }, []);

  // Order State, save all the info
  const [order, setOrder] = useState(
    getInitialState(
      mode,
      isOpportunity,
      owner,
      customer,
      [],
      opportunity,
      orderSubscription,
    ),
  );

  const [shippingProviders, setShippingProviders] = useState([]);
  const productExceededStock = !isOpportunity
    ? !!products.find(p => p.isOutOfStock)
    : false;
  const [isLoading, setIsLoading] = useState(false);

  const enableSubmit =
    !products?.find(p => p.notAvailableInCountry) &&
    !isLoading &&
    checkedTerms &&
    !productExceededStock &&
    order.billingAddress &&
    products.length > 0 &&
    (isOnlyDigitalProducts ? true : shippingProviders.length > 0);
  const [removeModal, showRemoveModal] = useModal({ show: false });

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
    if (loaderProducts.current) {
      if (isLoading) {
        loaderProducts.current.show();
      } else {
        loaderProducts.current.hide();
      }
    }
  }, [isLoading]);
  /** Handle state changes */

  // handle the order changes
  const handleChange = field => value => {
    setOrder(prevState => ({
      ...prevState,
      [field]: value,
    }));
  };

  // updates the different shipping (address and provider) sent by DisplayShippingContainer component in orderState
  const handleShipping = field => value => {
    setOrder(prevState => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleAddingProducts = useCallback(
    (productsList, allProducts = null) => {
      const productsToUpdate =
        productsList?.map(p => {
          const product = p;
          const { userPromotionPrice, userPrice } = product;
          // Get the correct userPrice to use (promo or not).
          // Pre-existing rows may not carry top-level userPrice; preserve their userPriceToUse.
          const resolvedPrice =
            userPromotionPrice?.price >= 0 ? userPromotionPrice : userPrice;
          product.userPriceToUse = resolvedPrice ?? product.userPriceToUse;
          // Quantity by default will be 1
          product.quantityToOrder = product?.quantityToOrder || 1;
          // It is used to identify which products are digital
          product.isDigitalContent = product?.parentProduct
            ? product?.parentProduct?.isDigital
            : product?.digitalDocument &&
              product?.digitalDocument?.url?.length > 0;
          if (product.packType === PACK_TYPE.HYBRID_PACK) {
            product.packVariations = [];
          }
          return product;
        }) || [];
      if (!allProducts) {
        // If popup sends products, update the state
        if (productsList) {
          setProducts(() => {
            // updates list with new products + previous products
            tableEl.current.setList([...productsToUpdate]);
            return productsToUpdate;
          });
        }
      } else {
        setWarningModal({
          show: true,
          data: { previous: allProducts, new: productsList },
        });
      }

      // closes popup
      setAddProductModal({
        show: false,
        data: null,
      });
    },
    [tableEl?.current, setAddProductModal, setProducts, products],
  );

  /**
   * Handles Click on Add New Product
   * Does validation to get user first and to make sure we have address selected
   * Opens popup
   * @type {(function(): void)|*}
   */
  const handleAddProduct = useCallback(() => {
    if (!order.seller) {
      Notifications.showNotificationError(
        t('error'),
        t('order.create.errorMsg.selectCustomerFirst'),
      );
      return;
    }
    // user don't have phone number && not have in address already saved one
    if (
      (!order?.seller?.phone || order.seller?.phone?.length === 0) &&
      (!order?.seller?.address?.phone ||
        order?.seller?.address?.phone?.length === 0)
    ) {
      Notifications.showNotificationError(
        t('error'),
        t('marketplace.shippingTab.error.phone'),
      );
      return;
    }
    setAddProductModal({ show: true });
  }, [order?.seller]);

  const getStockForVariations = productsInCheckout => {
    const stocksForCheckout = {};

    // eslint-disable-next-line no-unused-expressions
    productsInCheckout?.forEach(product => {
      const { isPack, packVariations } = product;

      if (isPack) {
        packVariations?.forEach(({ _id: key, stockQuantity, quantity }) => {
          const amount = quantity * (product?.quantityToOrder ?? 1);

          stocksForCheckout[key] = {
            stockQuantity,
            quantityInCheckout:
              (stocksForCheckout[key]?.quantityInCheckout || 0) + amount,
          };
        });
      } else {
        const key = product?._id;
        const stockQuantity = product?.stockQuantity;
        const amount = product?.quantityToOrder;

        stocksForCheckout[key] = {
          stockQuantity,
          quantityInCheckout:
            (stocksForCheckout[key]?.quantityInCheckout || 0) + amount,
        };
      }
    });
    return stocksForCheckout;
  };

  useEffect(() => {
    if (products.length > 0) {
      // Filter by the digital ones
      const hasDigitalInCart = products.filter(
        p => p.isDigitalContent === true,
      );
      // Check if all is digital by comparing the products cart length with the actually digital products in cart
      setIsOnlyDigitalProducts(hasDigitalInCart.length === products.length);
    }
  }, [products]);

  const shippingProviderIsValid = () => {
    let valid = false;
    // New array with valid shipping or undefined attr
    const shipping = shippingProviders.map(
      s =>
        (s.userPrice && s.userPrice.price) ||
        (s.userClickAndCollectPrice && s.userClickAndCollectPrice.price),
    );
    // if there is a shipping different of undefined, the prop will change to valid and the
    // shipping providers list will be rendered
    if (shipping.some(s => s !== undefined)) {
      valid = true;
    }
    return valid;
  };
  /** API REQUESTS */
  // POST - Calculate a shipping provider
  const calculateShippingProvider = (payer, receiver) => {
    // by default the receiver is the payer
    const localReceiver = receiver || payer;

    const params = {
      billingAddress: { ...order.billingAddress, NIF: undefined },
      shippingAddress: {
        ...(operatorAddress || order?.shippingAddress || order.billingAddress),
        NIF: undefined,
      },
      products: getProductsForOrderCreate(products),
      discount: order.discount,
      isPaidWithCoins: order.isPaidWithCoins,
    };

    let url = '/api/market/shipping-provider/calculate-shipping?';
    let country;
    if (
      !localReceiver?.personalInfo?.businessType ||
      localReceiver?.personalInfo?.businessType === 'individual'
    ) {
      country =
        localReceiver.shippingCountry || localReceiver?.address?.countryCode;
    } else {
      country =
        order.billingAddress?.countryCode ||
        localReceiver?.address?.countryCode ||
        localReceiver.shippingCountry;
    }
    if (country) {
      url += `&country=${country}`;
    }
    if (payer.currency) {
      url += `&currency=${payer.currency}`;
    }
    axios
      .post(url, params, {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setShippingProviders(response.data);
      })
      .catch(() => {});
  };

  useEffect(() => {
    let shouldCalculateShipping = true;

    const shippingAddress = operatorAddress || order.shippingAddress;
    const receiver = operator || order.seller;

    // Check the business type to see if need to check the shipping country
    if (
      !receiver?.personalInfo?.businessType ||
      receiver?.personalInfo.businessType === 'individual'
    ) {
      shouldCalculateShipping = receiver?.shippingCountry
        ? receiver?.shippingCountry === shippingAddress?.countryCode ||
          receiver?.shippingCountry === order?.billingAddress?.countryCode
        : true;
    }

    if (!isOnlyDigitalProducts) {
      if (
        order.billingAddress &&
        shouldCalculateShipping &&
        products.length > 0
      ) {
        calculateShippingProvider(order.seller, receiver);
      } else {
        setShippingProviders([]);
      }
    }
  }, [
    order.billingAddress,
    order.shippingAddress,
    operator,
    operatorAddress,
    products.length,
    products,
    isOnlyDigitalProducts,
  ]);

  // State for checkbox, select a different shipping address
  const [shippingAddressIsDifferent, setShippingAddressIsDifferent] = useState(
    getInitialShipping(mode, order, orderSubscription),
  );

  /** CHECK WITH THE API THE CORRECT VALUES TO DISPLAY IN ORDER */
  const [orderDetailPrices, setOrderDetailPrices] =
    useState(initialTotalsState);
  /** API REQUESTS */

  const handleDifferentShippingAddress = () => {
    setShippingAddressIsDifferent(prevState => {
      if (prevState) {
        // In case of turning to false clean shippingAddress
        setOrder(prevOrder => ({ ...prevOrder, shippingAddress: null }));
      }
      return !prevState;
    });
  };

  // API Request to get user info;
  const getUser = (customerFromChat = false) => {
    customerLoader.current.show();
    fetchUser(customerFromChat ? customer._id : order?.seller).then(
      response => {
        customerLoader.current.hide();
        if (response) {
          setOrder(prevState => ({
            ...prevState,
            discount: null,
            seller: response.data,
            billingAddress: response.data.address,
            shippingAddress: null,
            shipping: null,
            currency: response.data.currency ? response.data.currency : 'eur',
          }));
          if (!orderSubscription) {
            setShippingAddressIsDifferent(false);
          }
        }
      },
    );
  };

  // use effect responsible for getting the user selected
  useEffect(() => {
    if (!orderSubscription) {
      if (order?.seller && typeof order?.seller === 'string') {
        getUser();
      } else if (getCustomer) {
        setGetCustomer(false);
        getUser(true);
      }
    }
  }, [order?.seller]);

  // CONFIRM PAYMENT
  const confirmPayment = orderId => {
    if (paymentProviderType === PAYMENT_PROVIDER.STRIPE) {
      axios
        .post(
          `/api/payment/intent/market/order/${orderId}/confirm`,
          {},
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'License',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        )
        .then(() => {
          window.location.href = pageInformation.goBackUrl;
        })
        .catch(err => {
          Notifications.showErrorFromRequest(err, 'order_create_fail');
          console.log(err);
        });
    } else {
      window.location.href = pageInformation.goBackUrl;
    }
  };
  // POST AN ORDER
  const createOrder = sendEmail => {
    // Checks if the product list is empty
    if (products.length === 0) {
      Notifications.showErrorFromRequest(null, 'order_create_fail_no_products');
      return;
    }
    // Checks if there is a billing address
    if (order.billingAddress === null) {
      Notifications.showErrorFromRequest(
        null,
        'order_create_fail_no_billingAddress',
      );
      return;
    }
    const billingAddress = handleBillingAddressInOrder(
      order.seller,
      order.billingAddress,
    );
    const { businessType = null, companyName = null } =
      order.seller?.personalInfo || {};
    if (!(businessType === 'individual' || !businessType) && companyName) {
      billingAddress.companyName = companyName;
    }

    const customerShippingAddr = handleShippingAddressInOrder(
      order.seller,
      order?.shippingAddress,
    );
    const operatorShippingAddr = handleShippingAddressInOrder(
      operator,
      operatorAddress,
    );

    const receiver = operator || order.seller;
    const shippingAddress =
      operatorShippingAddr || customerShippingAddr || billingAddress;
    const params = {
      owner: order.seller._id,
      discount: order.discount,
      isPaidWithCoins: order.isPaidWithCoins,
      // Digital-only orders never ship: the shipping selector is hidden for
      // them, but a provider can still linger in state (e.g. carried over from
      // the edited subscription). Never send it, or it gets persisted onto the
      // subscription and charged on every renewal.
      shipping: isOnlyDigitalProducts ? null : order?.shipping?._id || null,
      currency: order.currency,
      // NIFs are added to the addresses for display purposes only
      // and need to be removed to pass the validator
      billingAddress: { ...billingAddress, NIF: undefined },
      shippingAddress: shippingAddress
        ? {
            ...shippingAddress,
            NIF: undefined,
          }
        : undefined,
      installationAddress: operatorShippingAddr
        ? { ...(customerShippingAddr || billingAddress), NIF: undefined }
        : undefined,
      operator: operator?._id,
      products: getProductsForOrderCreate(products, isOpportunity),
      adminNotes: order?.adminNotes || undefined,
      note: order?.note || undefined,
    };
    if (isOpportunity) {
      params.name = order.name;
      params.documents = order?.documents || [];
      params.status = order.status || OPPORTUNITIES_STATUS.PENDING;
      const formsNotValid = notValidProductForms(products);
      if (formsNotValid) {
        Notifications.showNotificationError(
          t('error'),
          t('form.error.not.filled', formsNotValid),
        );
        return;
      }
    }
    const hasProductWithFrequency = products?.find(p => p.frequency);
    if (hasProductWithFrequency?.frequency !== 'only_once') {
      params.frequency = hasProductWithFrequency?.frequency || null; // TODO: CHANGE WHEN MANAGING SUBSCRIPTIONS IN OPPORTUNITY
    }
    let country;
    if (
      !receiver?.personalInfo?.businessType ||
      receiver?.personalInfo?.businessType === 'individual'
    ) {
      country = receiver.shippingCountry || receiver?.address?.countryCode;
    } else {
      country =
        order.billingAddress?.countryCode ||
        receiver?.address?.countryCode ||
        receiver.shippingCountry;
    }
    let url = `${pageInformation.createEndpoint}sendEmail=${sendEmail ?? true}`;
    if (country) {
      url += `&country=${country}`;
    }
    if (order.seller.currency) {
      url += `&currency=${order.seller.currency}`;
    }

    if (
      !orderSubscription &&
      (orderDetailPrices.total === 0 ||
        paymentProviderType === PAYMENT_PROVIDER.STRIPE)
    ) {
      params.paymentMethodType = 'bank_transfer_manual';
    }

    if (isOpportunity && isEdit) {
      params.signature = null;
    }

    let errorPackProducts = {
      hasError: false,
    };
    const advancedPacks = products?.filter(p => p.packType === 'advanced');
    advancedPacks?.forEach(pack => {
      const { packVariations, amountVariationsPerAdvancedPack } = pack || {};
      const numberOfProducts =
        packVariations?.reduce(
          (acc, item) => acc + parseInt(item.quantity, 10),
          0,
        ) || 0;
      if (amountVariationsPerAdvancedPack !== numberOfProducts) {
        errorPackProducts = {
          hasError: true,
          amountVariationsPerAdvancedPack,
        };
      }
    });
    if (errorPackProducts?.hasError) {
      Notifications.showNotificationWarning(
        t('warning'),
        t(
          'advancedPack.error.amountVariationsPerAdvancedPack',
          errorPackProducts?.amountVariationsPerAdvancedPack,
        ),
      );
      setIsSendOfferModalLoading(false);
      setShowSendEmailModal({ show: false, data: null });
      return;
    }

    setIsLoading(true);

    axios[isEdit || orderSubscription ? 'put' : 'post'](url, params, {
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'Games',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    })
      .then(({ data: response }) => {
        const orderId = response.data._id;
        let successMessage = t('order_create_success');

        if (isOpportunity) {
          successMessage = sendEmail
            ? t('send.offer.email.sent')
            : t('send.offer.email.not.sent');
        }
        if (orderSubscription) {
          successMessage = pageInformation.successMessage;
        }
        Notifications.showNotificationSuccess(t('success'), successMessage);
        if (
          !isOpportunity &&
          orderId &&
          (response.data.total === 0 ||
            paymentProviderType === PAYMENT_PROVIDER.STRIPE)
        ) {
          let requestURL = `/api/payment/intent/market/order/${orderId}/capture`;

          if (paymentProviderType === PAYMENT_PROVIDER.STRIPE_INVOICES) {
            requestURL = `/api/order/market/${orderId}/capture_payment`;
          }
          axios
            .post(
              requestURL,
              {
                paymentMethod: 'bank_transfer_manual',
              },
              {
                headers: {
                  'Accept-Version': '1.0.0',
                  'X-Gamification-API': 'License',
                  'X-CSRF-Token': _csrf,
                  _csrf,
                },
              },
            )
            .then(() => {
              if (response.data.total === 0) {
                confirmPayment(orderId);
              } else {
                window.location.href = pageInformation.goBackUrl;
              }
            })
            .catch(err => {
              Notifications.showErrorFromRequest(err, 'order_create_fail');
              console.log(err);
            });
        } else {
          window.location.href = pageInformation.goBackUrl;
        }

        setIsLoading(false);
      })
      .catch(error => {
        Notifications.showErrorFromRequest(error, 'order_create_fail');
        setIsLoading(false);
      });
  };

  const openEmailPopup = useCallback(() => {
    setShowSendEmailModal({ show: true, data: null });
  }, []);

  const handleCreateOpportunity = useCallback(
    async (sendEmail, type) => {
      if (type === 'isCancel') {
        setShowSendEmailModal({ show: false, data: null });
        return;
      }
      setIsSendOfferModalLoading(true);
      const formsNotValid = notValidProductForms(products);
      if (formsNotValid) {
        Notifications.showNotificationError(
          t('error'),
          t('form.error.not.filled', formsNotValid),
        );
        setShowSendEmailModal({ show: false, data: null });
        return;
      }
      await createOrder(sendEmail);
    },
    [mode, setShowSendEmailModal, order, setIsSendOfferModalLoading, products],
  );

  const checkOrderDetail = (payer, receiver) => {
    // by default the receiver is the payer
    const localReceiver = payer || receiver;

    const params = {
      products: getProductsForOrderCreate(products),
      owner: order.seller._id,
      discount: order.discount,
      isPaidWithCoins: order.isPaidWithCoins,
    };
    if (order.billingAddress) {
      params.billingAddress = { ...order.billingAddress, NIF: undefined };
    }

    const shippingAddress = operatorAddress || order.shippingAddress;
    if (shippingAddress && shippingAddress.countryCode) {
      params.shippingAddress = { ...shippingAddress, NIF: undefined };
    } else if (params.billingAddress) {
      params.shippingAddress = params.billingAddress;
    }

    if (order.shipping) {
      params.shipping = order.shipping._id;
    }
    let country;
    if (
      !localReceiver?.personalInfo?.businessType ||
      localReceiver?.personalInfo?.businessType === 'individual'
    ) {
      country =
        localReceiver.shippingCountry || localReceiver?.address?.countryCode;
    } else {
      country =
        order.billingAddress?.countryCode ||
        localReceiver?.address?.countryCode ||
        localReceiver.shippingCountry;
    }
    let url = pageInformation.calculateEndpoint;
    if (country) {
      url += `&country=${country}`;
    }
    if (payer.currency) {
      url += `&currency=${payer.currency}`;
    }
    axios
      .post(url, params, {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        // Destructing response
        const { data } = response;
        const {
          pricesWithoutVAT,
          subtotal,
          userDiscountTotal,
          userDiscount,
          total,
          subtotalTax,
          shippingTotal,
          shippingTax,
          totalVA,
          totalCV,
          subtotalExtraTax,
        } = data;

        setOrderDetailPrices({
          subtotal,
          userDiscountPercentage: userDiscount,
          userDiscount: userDiscountTotal,
          total,
          tax: subtotalTax,
          pricesWithoutVAT,
          shippingTotal,
          shippingTax,
          shippingSubTotalTax: data.shippingSubTotalTax,
          shippingSubTotal: data.shippingSubTotal,
          totalVA,
          totalCV,
          currency: payer.currency,
          country,
          subtotalExtraTax,
          discountTotal: data.discountTotal,
          coinsDiscountTotal: data.coinsDiscountTotal || 0,
          shippingDiscountTotal: data.shippingDiscountTotal || 0,
        });
      })
      .catch(err => {
        if (err?.response?.data?.error === 'ERROR_COUPON_DISCOUNT_NOT_FOUND') {
          setOrder(prevOrder => ({
            ...prevOrder,
            discount: null,
          }));
        }
        Notifications.showErrorFromRequest(err, 'error_fetchProductsList');
        console.log(err);
      });
  };
  useEffect(() => {
    // we need to make sure that it's only checking if there are products in the state
    if (products.length > 0) {
      checkOrderDetail(order.seller, operator);
    }
  }, [
    products,
    operator,
    operatorAddress,
    order.seller,
    order.billingAddress,
    order.shippingAddress,
    order.shipping,
    order.discount,
    order.isPaidWithCoins,
  ]);

  const hasAddress =
    (operator && operatorAddress) ||
    (order.seller && (order?.shippingAddress || order.billingAddress));

  /** RENDER COLUMNS - List */
  function handleListInitialization() {
    const $tableSelector = $(`#${tableId}`);
    $('period_').select2();
    $tableSelector.on('click', '.remove-product-var', function edit() {
      const indexEvent = $(this).attr('data-product-variation-index');
      showRemoveModal({ show: true, index: indexEvent });
    });

    $tableSelector.on('change keyup', '.quantity-input', function (event) {
      event.preventDefault(); // Prevent the default behavior of the input event
      const $input = $(this); // Cache $(this) as $input for later use
      const newValue = parseInt($input.val(), 10);
      const id = $input.data('id'); // Use .data() method to get the 'data-id' attribute value

      clearTimeout($input.data('timeout')); // Clear previous timeout associated with this input

      const inputTimeout = setTimeout(() => {
        setProducts(prevState => {
          const newProductQuantity = prevState.map(item => {
            // If the item's _id matches the target id, update its quantityToOrder
            if (item._id === id) {
              return {
                ...item,
                quantityToOrder: newValue, // Replace newValue with the desired quantity
              };
            }
            // If it's not the target item, return the item unchanged
            return item;
          });
          const stocksForCheckout = getStockForVariations(newProductQuantity);
          tableEl.current.setList([
            ...newProductQuantity.map(product => {
              const newProduct = product;
              const { isPack, packVariations } = newProduct;

              if (isPack) {
                const variationOutOfStock = packVariations?.find(
                  v =>
                    stocksForCheckout[v?._id].stockQuantity &&
                    stocksForCheckout[v?._id].stockQuantity <
                      stocksForCheckout[v?._id].quantityInCheckout,
                );
                newProduct.isOutOfStock = !!variationOutOfStock;
              } else {
                newProduct.isOutOfStock =
                  stocksForCheckout[newProduct._id].stockQuantity &&
                  stocksForCheckout[newProduct._id].stockQuantity <
                    stocksForCheckout[newProduct._id].quantityInCheckout;
              }
              return newProduct;
            }),
          ]);
          return newProductQuantity;
        });
      }, 300);

      $input.data('timeout', inputTimeout); // Store the new timeout associated with this input
    });

    $tableSelector.on('change', '.period-input', function (event) {
      event.preventDefault(); // Prevent the default behavior of the input event
      const $input = $(this); // Cache $(this) as $input for later use
      const newValue = $input.val();
      const id = $input.data('id'); // Use .data() method to get the 'data-id' attribute value

      const previousFrequency = products.find(p => p._id === id)?.frequency;
      // Show warning when the new frequency conflicts with other products
      const otherProducts = products.filter(p => p._id !== id);
      const hasConflict = otherProducts.some(p => p.frequency !== newValue);
      if (hasConflict && products.length > 1) {
        setWarningModal({
          show: true,
          data: { productId: id, newFrequency: newValue },
        });
        $(this).val(previousFrequency);
      } else {
        const itemsMatched = products?.filter(
          p =>
            !p?.parentProduct?.availableForSubscription ||
            p.parentProduct?.availableSubscriptionFrequency?.includes(newValue),
        );
        // If we are trying to select only_once but some product's don't have it! It shouldn't be possible
        if (itemsMatched?.length !== products.length) {
          setWarningModal({
            show: true,
            data: { productId: id, newFrequency: newValue },
          });
          $(this).val(previousFrequency);
        } else {
          // success case, update selector and state
          setProducts(prevState => {
            const newProductQuantity = prevState.map(item => {
              // If the item's _id matches the target id, update its quantityToOrder
              if (item._id === id) {
                return {
                  ...item,
                  frequency: newValue, // Replace newValue with the desired quantity
                };
              }
              // If it's not the target item, return the item unchanged
              return item;
            });
            tableEl.current.setList([...newProductQuantity]);
            return newProductQuantity;
          });
          $input.val(newValue);
        }
      }
    });

    $tableSelector.on('click', '.btn-fill-form', function () {
      const $btn = $(this);
      const productId = $btn.data('product');

      // productsRef (not the stale closured `products`) so we get the latest
      // formAnswerData saved since this handler was bound — otherwise re-opening
      // a filled form shows it blank.
      const product = productsRef.current.find(p => p._id === productId);

      const isSurveyJSForm =
        product?.form?.providerType === PROVIDER_TYPE.SURVEY_JS;
      const formToModal = isSurveyJSForm
        ? {
            providerType: product?.form?.providerType,
            productId,
            formQuestionData: product?.form?.providerData,
            formAnswerData: product?.formAnswerData || null,
          }
        : {
            providerType: product?.form?.providerType,
            productId,
            formAnswerData: product?.formAnswerData || null,
            templatePDF: product?.form?.templatePDF,
            productForm: product?.form,
            // Always merge any in-progress answers back into the template so
            // re-opening the form restores what was filled — not just when
            // editing an existing order. (Falls back to the template's own
            // values when there's no saved answer yet.)
            providerData: {
              ...product?.form?.providerData,
              formFieldValues:
                product?.formAnswerData?.formFieldValues ||
                product?.form?.providerData?.formFieldValues,
            },
            formAnswersLink: product?.formAnswersLink,
          };
      setFormModal({
        show: true,
        data: formToModal,
      });
    });

    $tableSelector.on('click', '.btn-select-products', function () {
      const pack = $(this).attr('data-pack');
      setShowSelectVariationsModal({
        show: true,
        data: pack ? JSON.parse(pack) : null,
      });
    });

    $tableSelector.on('click', '.btn-products-inside', function () {
      const pack = $(this).attr('data-pack');
      setShowSelectVariationsModal({
        show: true,
        data: pack ? JSON.parse(pack) : null,
      });
    });
  }

  const handlePackProducts = useCallback(
    (productId, packVariations) => {
      setProducts(prevProducts => {
        const newProducts = prevProducts?.map(product => {
          if (product?._id === productId) {
            if (product?.packType === PACK_TYPE.HYBRID_PACK) {
              const totalVariations = [
                ...(product?.parentProduct?.packVariations || []),
                ...(packVariations?.map(v => ({
                  quantity: v.quantity,
                  _id: v._id,
                  variation: v,
                })) || []),
              ];

              const userPriceToUse = totalVariations.reduce(
                (acc, v) =>
                  acc +
                  (v.variation?.userPromotionPrice?.price ||
                    v.variation?.userPrice?.price ||
                    0) *
                    (v.quantity || 1),
                0,
              );
              const totalVA = totalVariations.reduce((acc, v) => {
                if (v?.variation?.va && v?.variation?.commissionable) {
                  return acc + (v.quantity || 1) * v.variation.va;
                }
                return acc;
              }, 0);
              const totalCV = totalVariations.reduce((acc, v) => {
                if (v?.variation?.cv && v?.variation?.commissionable) {
                  return acc + (v.quantity || 1) * v.variation.cv;
                }
                return acc;
              }, 0);

              return {
                ...product,
                packVariations,
                userPriceToUse: {
                  ...product.userPriceToUse,
                  price: userPriceToUse,
                },
                commissionable: totalVariations?.some(
                  v => v.variation?.commissionable,
                ),
                va: totalVA,
                cv: totalCV,
              };
            }

            return { ...product, packVariations };
          }
          return product;
        });
        tableEl.current.setList(newProducts);
        return newProducts;
      });
    },
    [setProducts],
  );
  const handleClosePackModal = useCallback(
    (productId, packVariations) => {
      setShowSelectVariationsModal({ show: false, data: null });
      if (packVariations?.length > 0) {
        handlePackProducts(productId, packVariations);
      }
    },
    [setShowSelectVariationsModal, handlePackProducts],
  );
  const handleRemoveModalClose = () => {
    showRemoveModal({ show: false, index: null });
  };
  const handleRemove = index => {
    showRemoveModal({ show: false, index: null });
    tableEl.current.removeListItem(index);
    setProducts(prevState => {
      prevState.splice(index, 1);
      // Filter by the digital ones
      const hasDigitalInCart = prevState.filter(
        p => p.isDigitalContent === true,
      );
      // Check if all is digital by comparing the products cart length with the actually digital products in cart
      setIsOnlyDigitalProducts(
        prevState.length > 0
          ? hasDigitalInCart.length === prevState.length
          : false,
      );
      return prevState;
    });
  };

  useEffect(() => {
    if (isOnlyDigitalProducts) {
      setOrder(prevState => ({
        ...prevState,
        shipping: null,
        shippingAddress: null,
      }));
    }
  }, [isOnlyDigitalProducts]);

  /** region Handle Pop ups */
  const handleAddUserClose = useCallback(
    createdUser => {
      if (createdUser) handleChange('seller')(createdUser._id);
    },
    [handleChange],
  );
  const handleAddressOpen = useCallback(
    (type, address, index) => {
      showAddressModal({ show: true, data: { address, type, index } });
    },
    [showAddressModal],
  );

  const handleAddOperatorAddress = useCallback(
    (type, address, index) => {
      showOperatorAddressModal({
        show: true,
        data: { address, type: index === 0 ? 'billing' : 'shipping', index },
      });
    },
    [showAddressModal],
  );

  const updateUserInformation = useCallback(
    async (user, additionalInformation, addressToSave) => {
      try {
        if (addressToSave === null) {
          showAddressModal({ show: false, data: null });
          return;
        }
        const { type, index } = additionalInformation?.data || {};
        const params = {};
        if (type === 'billing') {
          params.address = validateAddressFields(addressToSave);
          if (addressToSave?.phone) {
            params.phone = addressToSave?.phone;
          }
        }
        if (type === 'shipping') {
          const fullShippingAddresses = user?.shippingAddress;
          if (typeof index === 'number') {
            fullShippingAddresses[index] = validateAddressFields(addressToSave);
            params.shippingAddress = fullShippingAddresses;
          } else {
            params.shippingAddress = [
              ...fullShippingAddresses,
              validateAddressFields(addressToSave),
            ];
          }
        }
        const { data: response } = await axios.put(
          `/api/user/${user._id}/settings`,
          params,
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Organization',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        );
        // eslint-disable-next-line consistent-return
        return {
          userUpdated: response.data,
          changeType: type,
          address: addressToSave,
        };
      } catch (err) {
        console.error(err);
        Notifications.showErrorFromRequest(err);
        // eslint-disable-next-line consistent-return
        throw err;
      }
    },
    [],
  );

  const handleCloseOperatorAddressModal = useCallback(
    async addressToSave => {
      if (!addressToSave) {
        showOperatorAddressModal({ show: false, data: null });
        return;
      }
      operatorLoader.current.show();
      const { userUpdated } = await updateUserInformation(
        operator,
        operatorAddressModal,
        addressToSave,
      );
      setOperator(userUpdated);
      operatorLoader.current.hide();
      showOperatorAddressModal({ show: false, data: null });
    },
    [operatorAddressModal?.data, _csrf, operator?._id],
  );

  const handleAddressClose = useCallback(
    async addressToSave => {
      if (!addressToSave) {
        showAddressModal({ show: false, data: null });
        return;
      }
      const { userUpdated, changeType, address } = await updateUserInformation(
        order.seller,
        addressModal,
        addressToSave,
      );
      setOrder(prevState => ({
        ...prevState,
        billingAddress: userUpdated.address || prevState.billingAddress,
        shippingAddress:
          changeType === 'shipping' ? address : prevState.billingAddress,
        phone: userUpdated.phone || prevState.phone,
        seller: userUpdated,
      }));
      showAddressModal({ show: false, data: null });
    },
    [addressModal?.data, _csrf, order?.seller?._id, setOrder, showAddressModal],
  );
  const handleWarningModalClose = useCallback(
    (save, productsConfirmed) => {
      if (productsConfirmed?.new && productsConfirmed?.previous) {
        if (save) {
          setProducts(productsConfirmed?.new);
          tableEl.current.setList(productsConfirmed?.new);
        } else {
          setProducts(productsConfirmed?.previous);
          tableEl.current.setList(productsConfirmed?.previous);
        }
        setWarningModal({ show: false, data: null });
        return;
      }
      if (save) {
        // If data contains productId and newFrequency (from frequency selector change),
        // update compatible products to new frequency and remove incompatible ones
        if (productsConfirmed?.productId && productsConfirmed?.newFrequency) {
          const { productId, newFrequency } = productsConfirmed;
          const updatedProducts = products
            .map(product => {
              if (product._id === productId) {
                return { ...product, frequency: newFrequency };
              }
              // Keep products that support the new frequency
              if (
                product?.parentProduct?.availableSubscriptionFrequency?.includes(
                  newFrequency,
                )
              ) {
                return { ...product, frequency: newFrequency };
              }
              // Remove incompatible products
              return null;
            })
            .filter(p => p);
          setProducts(updatedProducts);
          tableEl.current.setList(updatedProducts);
        } else {
          const validatedProducts =
            handleValidationFrequencies(productsConfirmed);
          setProducts(validatedProducts);
          tableEl.current.setList(validatedProducts);
        }
      }
      setWarningModal({ show: false, data: null });
    },
    [setWarningModal, products, handleValidationFrequencies],
  );
  /** endregion Handle Pop ups */

  const handleGoBack = useCallback(() => {
    window.location.href = pageInformation.goBackUrl;
  }, [isOpportunity]);

  const columns = useMemo(() => {
    const result = [
      {
        field: 'Name',
        width: 250,
        textAlign: 'left',
        title: t('order_product_name'),
        sortable: false,
        overflow: 'visible',
        template: item =>
          renderToStaticMarkup(<ProductItem t={t} item={item} />),
      },
      {
        field: 'quantity',
        width: 80,
        textAlign: 'left',
        title: t('order_product_quantity'),
        sortable: false,
        overflow: 'visible',
        template: variation =>
          renderToStaticMarkup(
            <input
              min={1}
              className="form-control m-input quantity-input"
              id={`quantity_${variation._id}`}
              name={`quantity_${variation._id}`}
              type="number"
              value={variation.quantityToOrder}
              data-id={variation._id}
              data-stockQuantity={variation.stockQuantity}
              data-quantityToOrder={variation.quantityToOrder || 1}
            />,
          ),
      },
      {
        field: 'period',
        width: 180,
        textAlign: 'left',
        title: t('period'),
        sortable: false,
        overflow: 'visible',
        template: variation =>
          renderToStaticMarkup(
            <div>
              {variation?.parentProduct?.availableForSubscription ? (
                <select
                  className="form-control m-input period-input"
                  id={`period_${variation._id}`}
                  name={`period_${variation._id}`}
                  data-id={variation._id}
                >
                  {variation?.parentProduct?.availableSubscriptionFrequency?.map(
                    frequency => (
                      <option
                        value={frequency}
                        selected={frequency === variation?.frequency}
                      >
                        {t(`product.subscription.frequency.${frequency}`)}
                      </option>
                    ),
                  )}
                </select>
              ) : (
                '-'
              )}
            </div>,
          ),
      },
    ];

    if (isOpportunity) {
      result.push({
        field: 'form',
        title: t('form'),
        textAlign: 'left',
        width: 100,
        template: ({ form }) =>
          renderToStaticMarkup(
            <span className="form-text">{form?.name || '-'}</span>,
          ),
      });
    }

    result.push(
      {
        field: 'unitPrice',
        width: 80,
        textAlign: 'left',
        title: t('order_unit_price'),
        sortable: false,
        overflow: 'visible',
        template: ({ userPriceToUse }) =>
          renderToStaticMarkup(
            <>
              {formatPrice(
                userPriceToUse?.price ?? 0,
                findCurrencySymbol(
                  userPriceToUse?.code || order?.currency,
                  currencies,
                ),
              )}
            </>,
          ),
      },
      {
        field: 'vat',
        title: t('settings.configuration.modules.marketplace.tax'),
        textAlign: 'left',
        width: 50,
        template: ({ userPriceToUse }) =>
          renderToStaticMarkup(<span>{`${userPriceToUse?.tax ?? 0}%`}</span>),
      },
    );

    if (showVA) {
      result.push({
        field: 'va',
        title: t('commissions.va'),
        textAlign: 'left',
        width: 60,
        template: ({ va, quantityToOrder, commissionable }) =>
          renderToStaticMarkup(
            <span>
              {va && commissionable
                ? normalizeNumber(va * (quantityToOrder || 1), 2)
                : '-'}
            </span>,
          ),
      });
    }

    if (showCV) {
      result.push({
        field: 'cv',
        title: t('commissions.cv'),
        textAlign: 'left',
        width: 60,
        template: ({ cv, quantityToOrder, commissionable }) =>
          renderToStaticMarkup(
            <span>
              {cv && commissionable
                ? normalizeNumber(cv * (quantityToOrder || 1), 2)
                : '-'}
            </span>,
          ),
      });
    }
    result.push({
      field: 'total',
      title: t('order_item_subtotal'),
      textAlign: 'left',
      width: 60,
      template: ({ userPriceToUse, quantityToOrder }) =>
        renderToStaticMarkup(
          <div className="price-unit_column">
            {formatPrice(
              (userPriceToUse?.price ?? 0) * (quantityToOrder || 1),
              findCurrencySymbol(
                userPriceToUse?.code || order?.currency,
                currencies,
              ),
            )}
          </div>,
        ),
    });

    if (isOpportunity) {
      result.push({
        field: 'formBtn',
        title: '',
        textAlign: 'left',
        width: 150,
        template: product =>
          renderToStaticMarkup(
            product?.form ? <FormButton product={product} t={t} /> : null,
          ),
      });
    }

    result.push({
      field: 'actions',
      title: '',
      textAlign: 'left',
      width: 30,
      template: (item, index) =>
        renderToStaticMarkup(
          <button
            aria-label="trash"
            type="button"
            className="remove-product-var action-remove-style"
            data-product-variation-index={index}
          >
            <i className="fa fa-trash" />
          </button>,
        ),
    });

    return result;
  }, []);

  /** region Add Documents by updating the opportunity */
  const onAddDocument = useCallback(
    async params => {
      setIsLoadingBtn(true);
      setOrder(prevOrder => ({
        ...prevOrder,
        ...params,
      }));
      setIsLoadingBtn(false);
    },
    [order],
  );
  /** endregion Add Documents by updating the opportunity */

  /** region remove document */
  const onRemoveDocument = useCallback(
    async params => {
      setShowRemoveDocument({ show: true, data: params });
    },
    [setShowRemoveDocument],
  );
  const onClosePopup = useCallback(
    () => setShowRemoveDocument({ show: false, data: null }),
    [setShowRemoveDocument],
  );

  const removeDocument = useCallback(async () => {
    setIsLoadingModal(true);
    setOrder(prevOrder => ({
      ...prevOrder,
      documents: prevOrder?.documents?.filter(
        doc => doc.url !== showRemoveDocument?.data?._id,
      ),
    }));
    setShowRemoveDocument({ show: false, data: null });
    setIsLoadingModal(false);
  }, [order?.documents, showRemoveDocument]);
  /** endregion remove document */

  const handleCouponSelection = useCallback((coupon, ignore = false) => {
    setOrder(prevOrder => ({
      ...prevOrder,
      discount: prevOrder?.discount === coupon && !ignore ? null : coupon,
    }));
  }, []);

  const handleCoinsClick = isPaidWithCoins => {
    setOrder(prevOrder => ({
      ...prevOrder,
      isPaidWithCoins,
    }));
  };
  /** Render Component */

  useEffect(() => {
    const newProducts = products.map(p => {
      const { availableCountries = [] } = p?.parentProduct || {};

      if (
        availableCountries.length > 0 &&
        !availableCountries.includes(
          order?.shippingAddress?.countryCode ||
            order?.billingAddress?.countryCode,
        )
      ) {
        return { ...p, notAvailableInCountry: true };
      }
      return { ...p, notAvailableInCountry: false };
    });

    if (newProducts.length && tableEl.current) {
      setProducts(newProducts);
      tableEl.current.setList(newProducts);
    }
  }, [order?.shippingAddress?.countryCode, order?.billingAddress?.countryCode]);

  return (
    <>
      {showSelectVariationsModal?.show && (
        <AddPackProduct
          product={{
            ...showSelectVariationsModal?.data,
            pack: {
              packType: showSelectVariationsModal?.data?.packType,
              amountVariationsPerAdvancedPack:
                showSelectVariationsModal?.data
                  ?.amountVariationsPerAdvancedPack,
            },
          }}
          user={operator || order.seller}
          onClose={packVariations =>
            handleClosePackModal(
              showSelectVariationsModal?.data?._id,
              packVariations,
            )
          }
          selectedVariations={
            showSelectVariationsModal?.data?.packVariations || []
          }
          multiPackVariationsAvailable={
            showSelectVariationsModal?.data?.multiPackVariationsAvailable
          }
          isBySection={
            showSelectVariationsModal?.data?.multiPackVariationsAvailable
              ?.length > 0
          }
        />
      )}
      {showSendEmailModal.show ? (
        <BasicModal
          onClose={(save, data, type) =>
            handleCreateOpportunity(save, data, type)
          }
          id="showSendEmailModal"
          title={t('send.offer.email')}
          saveMessage={t('yes')}
          cancelMessage={t('no')}
          showCloseOnTop
          isLoading={isSendOfferModalLoading}
          disabled={isSendOfferModalLoading}
        >
          <span>{t('send.offer.email.question')}</span>
        </BasicModal>
      ) : null}
      {showRemoveDocument?.show ? (
        <RemoveDocumentModal
          isLoading={isLoadingModal}
          onClose={onClosePopup}
          onUpdate={removeDocument}
          documentName={showRemoveDocument?.data?.documents?.originalName}
        />
      ) : null}
      {formModal.show ? (
        <div>
          {formModal?.data?.providerType === PROVIDER_TYPE.SURVEY_JS ? (
            <FormModal
              form={formModal.data}
              onClose={(pId, answers) => handleFormSave(pId, answers)}
            />
          ) : (
            <PdfFormModal
              form={formModal.data}
              onClose={(pId, answers, link) =>
                handleFormSave(pId, answers, link)
              }
              opportunity={order}
              userToFillForm={order.seller}
            />
          )}
        </div>
      ) : null}
      {warningModal.show ? (
        <BasicModal
          onClose={handleWarningModalClose}
          id="notAllowedModal"
          saveMessage={t('yes')}
          data={warningModal.data}
        >
          <WarningSection />
        </BasicModal>
      ) : null}
      ›
      {addressModal.show && (
        <AddAddressModal
          vatTypes={vatTypes}
          onClose={handleAddressClose}
          countries={countries}
          userProfile={order?.seller}
          address={addressModal?.data?.address || AddAddressModal.defaultProp}
          isShippingAddress
          index={addressModal?.data?.index}
        />
      )}
      {removeModal.show && (
        <RemoveProductVariationModal
          onRemove={handleRemove}
          index={parseInt(removeModal.index, 10)}
          onClose={handleRemoveModalClose}
        />
      )}
      {operatorAddressModal.show && (
        <AddAddressModal
          vatTypes={vatTypes}
          onClose={handleCloseOperatorAddressModal}
          countries={countries}
          userProfile={operator}
          address={
            operatorAddressModal?.data?.address || AddAddressModal.defaultProp
          }
          isShippingAddress
          index={operatorAddressModal?.data?.index}
        />
      )}
      {addProductModal?.show ? (
        <AddNewProductModal
          onClose={handleAddingProducts}
          payer={order?.seller}
          receiver={operator}
          selectedShippingAddress={operatorAddress || order?.shippingAddress}
          categories={categories}
          catalogTypes={catalogTypes}
          productVariations={products}
          endpoint="/table/market/product/available/list?populate=form&sort=-name"
          activeSubscriptionFrequency={
            products?.find(p => p.frequency !== 'only_once')?.frequency || null
          }
        />
      ) : null}
      <Title title={pageInformation.title}>
        <ContentContainer>
          <SubHeader title={pageInformation.title} />
          <div className="m-content shop products-toPay-container">
            <div className="m-portlet__body">
              <Form
                id={formId}
                submitMethod={!isOpportunity ? createOrder : openEmailPopup}
                validationRules={validationRules}
              >
                {submitHandler => (
                  <div className="justify-content-center">
                    <CustomerSection
                      order={order}
                      onChange={handleChange}
                      isOnlyDigitalProducts={isOnlyDigitalProducts}
                      onShippingChange={handleShipping}
                      isShippingAddressDifferent={shippingAddressIsDifferent}
                      onDifferentShippingAddress={
                        handleDifferentShippingAddress
                      }
                      userTypeNames={userTypeNames}
                      onCloseAddUser={handleAddUserClose}
                      onAddressOpen={handleAddressOpen}
                      isOpportunity={isOpportunity}
                      languages={languages}
                      mode={mode}
                      isSubscription={!!orderSubscription}
                    />
                    <div id="products" className="col-12 products-container">
                      <div className="">
                        <h4 className="order-card-title">
                          {t('shop.cart.products')}
                        </h4>
                        <div className="col-lg-12 products-mobile">
                          <List
                            id={tableId}
                            ref={tableEl}
                            loading={false}
                            data={products || []}
                            columns={columns}
                            onInit={handleListInitialization}
                            pagination={false}
                            onDataUpdated={() => {}}
                          />
                        </div>
                        <div className="col-lg-12 button-add-newProduct">
                          <button
                            type="button"
                            onClick={handleAddProduct}
                            className="btn btn-dynamic m-btn m-btn--icon
                               m-btn--icon-only m-btn--custom m-btn--outline-2x
                               m-btn--pill m-btn--air form"
                          >
                            <i className="la la-plus" />
                            <span style={{ minWidth: '200px' }}>
                              {t('order_add_new_product')}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                    {!orderSubscription?._id && (
                      <DocumentsTab
                        order={{
                          ...order,
                          documents: order?.documents || [],
                        }}
                        onAddDocument={onAddDocument}
                        isLoadingBtn={isLoadingBtn}
                        onRemoveClick={onRemoveDocument}
                        wrapperClass="col-12 opportunity-document-create"
                      />
                    )}
                    {displayOperators && !orderSubscription?._id && (
                      <OperatorSection
                        handleAddOperatorAddress={handleAddOperatorAddress}
                        handleAddressOpen={handleAddOperatorAddress}
                        operator={operator}
                        userTypeNames={userTypeNames}
                        handleOperatorChange={handleOperatorChange}
                        setOperatorAddress={setOperatorAddress}
                        languages={languages}
                      />
                    )}
                    {!isOnlyDigitalProducts && (
                      <div className="m-portlet m-portlet--mobile col-lg-12 col-12">
                        <div className="m-portlet__body shippingProvider-wrapper">
                          <h4 className="order-card-title">
                            {t('shipping_providers')}
                          </h4>
                          <div className="create-order-shipping-provider col-12">
                            {shippingProviders.length > 0 &&
                            shippingProviderIsValid() ? (
                              <DisplayShippingContainer
                                shipping={shippingProviders}
                                shippingSelected={order?.shipping?._id || null}
                                isProvider
                                currencies={currencies}
                                onShippingChange={handleShipping('shipping')}
                              />
                            ) : (
                              <span className="customer-info-invalid">
                                {hasAddress && products.length > 0
                                  ? t('order.providers_not_delivery')
                                  : t('order.noShippingProvider')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    <OrderCreateFooter
                      isOpportunity={isOpportunity}
                      order={order}
                      products={products}
                      orderDetailPrices={orderDetailPrices}
                      showVA={showVA}
                      showCV={showCV}
                      onCouponSelection={handleCouponSelection}
                      onCoinsSelection={handleCoinsClick}
                      currencies={currencies}
                      enableSubmit={enableSubmit}
                      handleGoBack={handleGoBack}
                      pageInformation={pageInformation}
                      submitHandler={submitHandler}
                      onChange={handleChange}
                      checkedTerms={checkedTerms}
                      onCheckboxChange={() => {
                        setCheckedTerms(prevTerms => !prevTerms);
                      }}
                    />
                  </div>
                )}
              </Form>
            </div>
          </div>
        </ContentContainer>
      </Title>
    </>
  );
}

OrderCreate.propTypes = {
  t: PropTypes.func.isRequired,
  owner: PropTypes.string.isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  _csrf: PropTypes.string.isRequired,
  customer: PropTypes.shape(userProp.propType),
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape(CategoryProp.propType))
    .isRequired,
  catalogTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      type: PropTypes.string,
      isActive: PropTypes.bool,
      translations: PropTypes.shape({}),
    }),
  ).isRequired,
  isOpportunity: PropTypes.bool,
  languages: PropTypes.shape(CountryProp.propType).isRequired,
  mode: PropTypes.string,
  opportunity: PropTypes.shape(OrderProp.propType).isRequired,
  orderSubscription: PropTypes.shape(SubscriptionProp.propType),
};

OrderCreate.defaultProps = {
  customer: null,
  isOpportunity: false,
  mode: 'create',
  orderSubscription: null,
};

export default translate(OrderCreate);
